import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  ForecastResultPoint,
  ForecastSummary,
} from "../../../../common/types/Forecast";
import { createServiceBaseQuery } from "../../client/serviceBaseQuery";

// Forecast reads go through nav's BFF (/api/forecasts/*), which owns the SQL and row→DTO
// mapping. The client just consumes typed DTOs.
export const forecastsService = createApi({
  reducerPath: "forecastsService",
  baseQuery: createServiceBaseQuery("bffApiUri"),
  tagTypes: ["Forecast"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getForecastSummary: builder.query<ForecastSummary[], void>({
      query: () => ({ url: "/api/forecasts/summary" }),
      providesTags: [{ type: "Forecast", id: "SUMMARY" }],
    }),

    getForecastResults: builder.query<
      ForecastResultPoint[],
      { forecastId: string; location: string | null }
    >({
      query: ({ forecastId, location }) => ({
        url: "/api/forecasts/results",
        // Omit location when null so the BFF treats it as IS NULL (forecasts not tied to a venue).
        params: location === null ? { forecastId } : { forecastId, location },
      }),
      providesTags: (_result, _err, { forecastId, location }) => [
        { type: "Forecast", id: `RESULTS-${forecastId}-${location ?? "null"}` },
      ],
    }),

    // Delete one location's slice of a forecast (Zeus cascades to a full teardown when it was the
    // last location). locationId is the stable id from the summary (not the display name); null is
    // the venue-less ("All locations") forecast.
    deleteForecast: builder.mutation<
      void,
      { forecastId: string; locationId: string | null }
    >({
      query: ({ forecastId, locationId }) => ({
        url: "/api/forecasts/delete",
        method: "POST",
        // Zeus contract: { forecastId, locationId }. We always send locationId explicitly (null
        // for the venue-less card).
        body: { forecastId, locationId },
      }),
      // No invalidatesTags on purpose: the delete takes a few seconds to propagate, so refetching
      // the summary now would resurrect the just-removed card. Instead we splice it out of the
      // summary cache once the 204 confirms (pessimistic), leaving a failed delete's card in place
      // (surfaced via snackbar). getForecastSummary takes no args, so its cache key is undefined.
      async onQueryStarted({ forecastId, locationId }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          forecastsService.util.updateQueryData(
            "getForecastSummary",
            undefined,
            (draft) => {
              const index = draft.findIndex(
                (card) =>
                  card.forecastId === forecastId && card.locationId === locationId,
              );
              if (index !== -1) draft.splice(index, 1);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetForecastSummaryQuery,
  useGetForecastResultsQuery,
  useDeleteForecastMutation,
} = forecastsService;
